import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Modal, Avatar } from "../components/Layout";
import { fmtDate } from "../lib/dates";
import { STEP_STATUS, SUPPORT_OPTIONS, REFLECT_OUTCOMES, RESPONSE_KINDS } from "../lib/bouquet";
import { Plus, Lock, Users, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export default function Growing() {
  const [intentions, setIntentions] = useState([]);
  const [pods, setPods] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ text: "", step: "", target_date: "", private_notes: "" });
  const [shareFor, setShareFor] = useState(null);
  const [sharePod, setSharePod] = useState("");
  const [sharePrefs, setSharePrefs] = useState([]);
  const [shareCommunity, setShareCommunity] = useState(false);
  const [reflectFor, setReflectFor] = useState(null);
  const [reflect, setReflect] = useState({ outcome: "", note: "", next_action: "create", new_step_text: "", new_target_date: "" });
  const [newStepFor, setNewStepFor] = useState(null);
  const [newStep, setNewStep] = useState({ text: "", target_date: "" });
  const [editIntention, setEditIntention] = useState(null);
  const [editIntentionDraft, setEditIntentionDraft] = useState({ text: "", private_notes: "" });
  const [editStep, setEditStep] = useState(null);
  const [editStepDraft, setEditStepDraft] = useState({ text: "", target_date: "" });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemTitle, setEditingItemTitle] = useState("");
  const [itemInputs, setItemInputs] = useState({});
  const location = useLocation();

  const load = () => {
    api.get("/intentions/my").then((r) => setIntentions(r.data));
    api.get("/pods/mine").then((r) => setPods(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    if (location.state?.create) {
      setShowCreate(true);
      window.history.replaceState({}, "");
      return;
    }
    if (location.state?.reflect && intentions.length) {
      for (const i of intentions) {
        const s = (i.steps || []).find((x) => x.step_id === location.state.reflect);
        if (s) { openReflect(i, s); break; }
      }
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, intentions]);

  const create = async () => {
    if (!draft.text.trim() || !draft.step.trim()) return toast.error("Name what's growing and one next step");
    try {
      const res = await api.post("/intentions", { text: draft.text, private_notes: draft.private_notes });
      await api.post(`/intentions/${res.data.intention_id}/steps`, { text: draft.step, target_date: draft.target_date });
      toast.success("Planted — private until you choose to share it");
      setShowCreate(false);
      setDraft({ text: "", step: "", target_date: "", private_notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const addStep = async () => {
    if (!newStep.text.trim()) return toast.error("What's the next step?");
    await api.post(`/intentions/${newStepFor.intention_id}/steps`, newStep);
    toast.success("Next step declared");
    setNewStepFor(null);
    setNewStep({ text: "", target_date: "" });
    load();
  };

  const addItem = async (s) => {
    const title = (itemInputs[s.step_id] || "").trim();
    if (!title) return;
    await api.post(`/steps/${s.step_id}/items`, { title });
    setItemInputs({ ...itemInputs, [s.step_id]: "" });
    load();
  };

  const toggleItem = async (s, item) => {
    await api.put(`/steps/${s.step_id}/items/${item.item_id}`, { done: !item.done });
    load();
  };

  const deleteItem = async (s, item) => {
    await api.delete(`/steps/${s.step_id}/items/${item.item_id}`);
    load();
  };

  const openShare = (i, s) => { setShareFor({ intention: i, step: s }); setSharePod(""); setSharePrefs([]); setShareCommunity(false); };

  const submitShare = async () => {
    if (!sharePod) return toast.error("Choose a pod to share with");
    try {
      await api.post(`/steps/${shareFor.step.step_id}/share`, { pod_id: sharePod, support_preferences: sharePrefs, also_community: shareCommunity });
      toast.success(shareCommunity ? "Shared with your pod and community — you can withdraw anytime" : "Shared with your pod — you can withdraw anytime");
      setShareFor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not share");
    }
  };

  const withdraw = async (s) => {
    await api.post(`/steps/${s.step_id}/withdraw`);
    toast.success("Back to private");
    load();
  };

  const openReflect = (i, s) => {
    setReflectFor({ intention: i, step: s });
    setReflect({ outcome: "", note: "", next_action: "create", new_step_text: "", new_target_date: "" });
  };

  const submitReflect = async () => {
    if (!reflect.outcome) return toast.error("Pick whatever feels true");
    try {
      await api.post(`/steps/${reflectFor.step.step_id}/reflect`, reflect);
      toast.success("Reflected. Movement counts — whatever shape it takes.");
      setReflectFor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save reflection");
    }
  };

  const setIntentionStatus = async (i, status) => {
    await api.put(`/intentions/${i.intention_id}`, { status });
    toast.success(status === "paused" ? "Paused — it'll be here when you're ready" : "Closed with care");
    load();
  };

  const openEditIntention = (i) => {
    setEditIntention(i);
    setEditIntentionDraft({ text: i.text, private_notes: i.private_notes || "" });
  };

  const saveEditIntention = async () => {
    if (!editIntentionDraft.text.trim()) return toast.error("Intention text required");
    try {
      await api.put(`/intentions/${editIntention.intention_id}`, {
        text: editIntentionDraft.text.trim(),
        private_notes: editIntentionDraft.private_notes,
      });
      toast.success("Intention updated");
      setEditIntention(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const openEditStep = (s) => {
    setEditStep(s);
    setEditStepDraft({ text: s.text, target_date: s.target_date || "" });
  };

  const saveEditStep = async () => {
    if (!editStepDraft.text.trim()) return toast.error("Step text required");
    try {
      await api.put(`/steps/${editStep.step_id}`, { text: editStepDraft.text.trim(), target_date: editStepDraft.target_date });
      toast.success("Next step updated");
      setEditStep(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const startEditItem = (item) => { setEditingItemId(item.item_id); setEditingItemTitle(item.title); };

  const saveEditItem = async (s, item) => {
    const title = editingItemTitle.trim();
    if (!title || title === item.title) { setEditingItemId(null); return; }
    await api.put(`/steps/${s.step_id}/items/${item.item_id}`, { title });
    setEditingItemId(null);
    load();
  };

  const activeStep = (i) => (i.steps || []).filter((s) => s.status === "active").slice(-1)[0];

  return (
    <div data-testid="growing-page" className="space-y-8">
      <PageHeader eyebrow="What's Growing" title="Intentions & Next Steps"
        description="Name what matters, choose one next step, and invite support on your terms. You own the definition of progress.">
        <button data-testid="new-intention-button" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
          <Plus className="h-4 w-4" /> Name what's growing
        </button>
      </PageHeader>

      {intentions.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-10 text-center space-y-3">
          <p className="font-display text-xl font-medium">What's growing for you right now?</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">"I'm finally ready to launch my consulting business." Anything that matters counts.</p>
        </div>
      )}

      <div className="space-y-4">
        {intentions.map((i) => {
          const current = activeStep(i);
          return (
            <div key={i.intention_id} className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid={`intention-card-${i.intention_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-display text-lg font-medium">{i.text}</p>
                  {i.status !== "active" && (
                    <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-secondary text-[11px] font-semibold capitalize">{i.status}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 text-xs font-semibold">
                  <button data-testid={`edit-intention-${i.intention_id}`} onClick={() => openEditIntention(i)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  {i.status === "active" && (
                    <>
                      <button data-testid={`pause-intention-${i.intention_id}`} onClick={() => setIntentionStatus(i, "paused")}
                        className="text-muted-foreground hover:text-foreground transition-colors">Pause</button>
                      <button data-testid={`close-intention-${i.intention_id}`} onClick={() => setIntentionStatus(i, "closed")}
                        className="text-muted-foreground hover:text-foreground transition-colors">Close</button>
                    </>
                  )}
                </div>
              </div>

              {i.private_notes && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Lock className="h-3 w-3" /> {i.private_notes} <span className="italic">(only you see this)</span>
                </p>
              )}

              <div className="space-y-2.5">
                {(i.steps || []).map((s) => (
                  <div key={s.step_id} className={`rounded-lg border p-4 space-y-2.5 ${s.status === "active" ? "border-primary/40 bg-secondary/40" : "border-border"}`}
                    data-testid={`step-row-${s.step_id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium flex-1 min-w-[160px]">{s.text}</p>
                      <span className="px-2 py-0.5 rounded-full bg-secondary text-[11px] font-semibold" data-testid={`step-status-${s.step_id}`}>
                        {STEP_STATUS[s.status] || s.status}
                      </span>
                      {s.target_date && <span className="text-[11px] text-muted-foreground">by {fmtDate(s.target_date)}</span>}
                      <button data-testid={`edit-step-${s.step_id}`} onClick={() => openEditStep(s)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      {s.shared_pod_name ? (
                        <span className="flex items-center gap-1 text-primary font-semibold" data-testid={`step-supported-${s.step_id}`}>
                          <Users className="h-3 w-3" /> Supported by {s.shared_pod_name}{s.shared_chapter_name ? ` · ${s.shared_chapter_name}` : ""}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground"><Lock className="h-3 w-3" /> Private</span>
                      )}
                      {s.status === "active" && (
                        <span className="flex gap-2 ml-auto">
                          {s.shared_pod_name ? (
                            <button data-testid={`step-withdraw-${s.step_id}`} onClick={() => withdraw(s)}
                              className="px-3 py-1.5 rounded-full border border-border font-semibold hover:bg-secondary transition-colors">Withdraw</button>
                          ) : (
                            <button data-testid={`step-share-${s.step_id}`} onClick={() => openShare(i, s)}
                              className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">Share with a pod</button>
                          )}
                          <button data-testid={`step-reflect-${s.step_id}`} onClick={() => openReflect(i, s)}
                            className="px-3 py-1.5 rounded-full border border-border font-semibold hover:bg-secondary transition-colors">How did it go?</button>
                        </span>
                      )}
                    </div>
                    {s.status === "active" && (
                      <div className="space-y-1.5" data-testid={`step-items-${s.step_id}`}>
                        {(s.items || []).length > 0 && (
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Steps · {(s.items || []).filter((x) => x.done).length}/{s.items.length}
                          </p>
                        )}
                        {(s.items || []).map((item) => (
                          <div key={item.item_id} className="flex items-center gap-2" data-testid={`item-row-${item.item_id}`}>
                            <button data-testid={`item-toggle-${item.item_id}`} onClick={() => toggleItem(s, item)}
                              className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${item.done ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                              {item.done && <Check className="h-3 w-3" />}
                            </button>
                            {editingItemId === item.item_id ? (
                              <input data-testid={`item-edit-input-${item.item_id}`} value={editingItemTitle}
                                onChange={(e) => setEditingItemTitle(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") saveEditItem(s, item); if (e.key === "Escape") setEditingItemId(null); }}
                                onBlur={() => saveEditItem(s, item)}
                                autoFocus
                                className="flex-1 min-w-0 px-2 py-0.5 rounded border border-input bg-background text-xs focus:outline-none focus:ring-2 focus:ring-ring" />
                            ) : (
                              <span className={`text-xs flex-1 min-w-0 truncate ${item.done ? "line-through text-muted-foreground" : ""}`}>{item.title}</span>
                            )}
                            <button data-testid={`item-edit-${item.item_id}`} onClick={() => startEditItem(item)}
                              className="text-muted-foreground hover:text-foreground px-1 shrink-0"><Pencil className="h-3 w-3" /></button>
                            <button data-testid={`item-delete-${item.item_id}`} onClick={() => deleteItem(s, item)}
                              className="text-muted-foreground hover:text-destructive px-1 shrink-0">×</button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input data-testid={`item-input-${s.step_id}`} value={itemInputs[s.step_id] || ""}
                            onChange={(e) => setItemInputs({ ...itemInputs, [s.step_id]: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && addItem(s)}
                            placeholder="Add a small step…"
                            className="flex-1 px-3 py-1.5 rounded-lg border border-input bg-background text-xs focus:outline-none focus:ring-2 focus:ring-ring" />
                          <button data-testid={`item-add-${s.step_id}`} onClick={() => addItem(s)}
                            className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition-colors">Add</button>
                        </div>
                      </div>
                    )}
                    {(s.responses || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`step-responses-${s.step_id}`}>
                        {s.responses.map((r, idx) => (
                          <span key={idx} className="px-2.5 py-1 rounded-full bg-accent/15 border border-accent/30 text-[11px] font-medium">
                            {r.name.split(" ")[0]} · {RESPONSE_KINDS.find((k) => k.kind === r.kind)?.label || r.kind}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {i.status === "active" && !current && (
                <button data-testid={`add-step-${i.intention_id}`} onClick={() => { setNewStepFor(i); setNewStep({ text: "", target_date: "" }); }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Choose a next step
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} testid="create-intention-modal">
          <div className="space-y-5">
            <div>
              <h3 className="font-display text-xl font-medium">What's growing for you right now?</h3>
              <p className="text-xs text-muted-foreground mt-1">Natural language is perfect. No categories, no scores.</p>
            </div>
            <textarea data-testid="intention-text-input" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              rows={2} placeholder="I'm finally ready to launch my consulting business." className={inputCls} />
            <div>
              <p className="text-sm font-semibold mb-1.5">What's one next step you want to take?</p>
              <input data-testid="step-text-input" value={draft.step} onChange={(e) => setDraft({ ...draft, step: e.target.value })}
                placeholder="Finish my service offering by Friday." className={inputCls} />
            </div>
            <div>
              <p className="text-sm font-semibold mb-1.5">Target date <span className="font-normal text-muted-foreground">(optional)</span></p>
              <input data-testid="step-date-input" type="date" value={draft.target_date} onChange={(e) => setDraft({ ...draft, target_date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <p className="text-sm font-semibold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Private notes <span className="font-normal text-muted-foreground">(only you, ever)</span></p>
              <textarea data-testid="intention-notes-input" value={draft.private_notes} onChange={(e) => setDraft({ ...draft, private_notes: e.target.value })}
                rows={2} placeholder="Anything you want to remember…" className={inputCls} />
            </div>
            <button data-testid="create-intention-submit" onClick={create}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Plant it — privately</button>
          </div>
        </Modal>
      )}

      {shareFor && (
        <Modal onClose={() => setShareFor(null)} testid="share-modal">
          <div className="space-y-5">
            <div>
              <h3 className="font-display text-xl font-medium">Share with a pod</h3>
              <p className="text-xs text-muted-foreground mt-1">"{shareFor.step.text}" · Only the pod you choose will see this. Your private notes are never included. You can withdraw anytime.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold">Which pod?</p>
              <div className="flex flex-wrap gap-2">
                {pods.map((p) => (
                  <button key={p.pod_id} data-testid={`share-pod-${p.pod_id}`} onClick={() => setSharePod(p.pod_id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${sharePod === p.pod_id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                    {p.name}
                  </button>
                ))}
                {pods.length === 0 && <p className="text-sm text-muted-foreground">You're not in a pod yet — create one from the Pods page.</p>}
              </div>
            </div>
            {sharePod && pods.find((p) => p.pod_id === sharePod)?.chapter_name && (
              <button data-testid="share-community-toggle" onClick={() => setShareCommunity((v) => !v)}
                className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border text-left text-xs font-medium transition-colors ${shareCommunity ? "bg-primary/10 border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${shareCommunity ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                  {shareCommunity && <Check className="h-3 w-3" />}
                </span>
                Also share with my community, {pods.find((p) => p.pod_id === sharePod)?.chapter_name}
              </button>
            )}
            <div className="space-y-2">
              <p className="text-sm font-semibold">How would you like your people to show up? <span className="font-normal text-muted-foreground">(optional, pick any)</span></p>
              <div className="flex flex-wrap gap-2">
                {SUPPORT_OPTIONS.map((o) => (
                  <button key={o} data-testid={`support-pref-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setSharePrefs((p) => p.includes(o) ? p.filter((x) => x !== o) : [...p, o])}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${sharePrefs.includes(o) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                    {sharePrefs.includes(o) && <Check className="h-3 w-3" />}
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="share-submit-button" onClick={submitShare}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">{shareCommunity ? "Share with pod + community" : "Share with pod"}</button>
          </div>
        </Modal>
      )}

      {reflectFor && (
        <Modal onClose={() => setReflectFor(null)} testid="reflect-modal">
          <div className="space-y-5">
            <div>
              <h3 className="font-display text-xl font-medium">How did it go?</h3>
              <p className="text-xs text-muted-foreground mt-1">"{reflectFor.step.text}" · There's no wrong answer. Movement counts, not compliance.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {REFLECT_OUTCOMES.map((o) => (
                <button key={o.key} data-testid={`outcome-${o.key}`} onClick={() => setReflect({ ...reflect, outcome: o.key })}
                  className={`px-3.5 py-2 rounded-full text-xs font-medium border transition-colors ${reflect.outcome === o.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <textarea data-testid="reflect-note-input" value={reflect.note} onChange={(e) => setReflect({ ...reflect, note: e.target.value })}
              rows={2} placeholder="A short reflection (optional, always private)…" className={inputCls} />
            <div className="space-y-2">
              <p className="text-sm font-semibold">What's your next step?</p>
              <div className="grid grid-cols-2 gap-2">
                {[["create", "A new next step"], ["continue", "Keep going with this one"], ["pause", "Pause for now"], ["close", "Close this intention"]].map(([key, label]) => (
                  <button key={key} data-testid={`next-action-${key}`} onClick={() => setReflect({ ...reflect, next_action: key })}
                    className={`px-3 py-2.5 rounded-lg border text-xs font-semibold transition-colors text-left ${reflect.next_action === key ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {reflect.next_action === "create" && (
              <div className="space-y-3">
                <input data-testid="reflect-new-step-input" value={reflect.new_step_text} onChange={(e) => setReflect({ ...reflect, new_step_text: e.target.value })}
                  placeholder="My next step is…" className={inputCls} />
                <input data-testid="reflect-new-date-input" type="date" value={reflect.new_target_date} onChange={(e) => setReflect({ ...reflect, new_target_date: e.target.value })} className={inputCls} />
              </div>
            )}
            {reflect.next_action === "continue" && (
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">New target date (optional)</p>
                <input data-testid="reflect-continue-date-input" type="date" value={reflect.new_target_date} onChange={(e) => setReflect({ ...reflect, new_target_date: e.target.value })} className={inputCls} />
              </div>
            )}
            <button data-testid="reflect-submit-button" onClick={submitReflect}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Save reflection</button>
          </div>
        </Modal>
      )}

      {newStepFor && (
        <Modal onClose={() => setNewStepFor(null)} testid="new-step-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Next step for "{newStepFor.text}"</h3>
            <input data-testid="new-step-text-input" value={newStep.text} onChange={(e) => setNewStep({ ...newStep, text: e.target.value })}
              placeholder="What's one next step?" className={inputCls} />
            <input data-testid="new-step-date-input" type="date" value={newStep.target_date} onChange={(e) => setNewStep({ ...newStep, target_date: e.target.value })} className={inputCls} />
            <button data-testid="new-step-submit" onClick={addStep}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Declare it</button>
          </div>
        </Modal>
      )}

      {editIntention && (
        <Modal onClose={() => setEditIntention(null)} testid="edit-intention-modal">
          <div className="space-y-5">
            <div>
              <h3 className="font-display text-xl font-medium">Edit intention</h3>
              <p className="text-xs text-muted-foreground mt-1">It's yours — change it whenever it changes.</p>
            </div>
            <textarea data-testid="edit-intention-text-input" value={editIntentionDraft.text}
              onChange={(e) => setEditIntentionDraft({ ...editIntentionDraft, text: e.target.value })}
              rows={2} placeholder="What's growing for you?" className={inputCls} />
            <div>
              <p className="text-sm font-semibold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Private notes <span className="font-normal text-muted-foreground">(only you, ever)</span></p>
              <textarea data-testid="edit-intention-notes-input" value={editIntentionDraft.private_notes}
                onChange={(e) => setEditIntentionDraft({ ...editIntentionDraft, private_notes: e.target.value })}
                rows={2} placeholder="Anything you want to remember…" className={inputCls} />
            </div>
            <button data-testid="edit-intention-save" onClick={saveEditIntention}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Save changes</button>
          </div>
        </Modal>
      )}

      {editStep && (
        <Modal onClose={() => setEditStep(null)} testid="edit-step-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Edit next step</h3>
            <input data-testid="edit-step-text-input" value={editStepDraft.text}
              onChange={(e) => setEditStepDraft({ ...editStepDraft, text: e.target.value })}
              placeholder="What's the next step?" className={inputCls} />
            <div>
              <p className="text-sm font-semibold mb-1.5">Target date <span className="font-normal text-muted-foreground">(optional)</span></p>
              <input data-testid="edit-step-date-input" type="date" value={editStepDraft.target_date}
                onChange={(e) => setEditStepDraft({ ...editStepDraft, target_date: e.target.value })} className={inputCls} />
            </div>
            <button data-testid="edit-step-save" onClick={saveEditStep}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Save changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
